import { NotificationTheme, SectionHelper, SectionHelperSkin } from '@wix/design-system';
import { StatusAlert, StatusComplete, StatusWarning } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, { FC, useEffect } from 'react';

import { floatingBannerDataHooks } from './FloatingBanner.dataHooks';
import s from './styles.scss';

export enum FloatingBannerOrigin {
  Home = 'home',
  Chat = 'chat',
  FullPage = 'fullPage',
}

export type FloatingBannerProps = {
  children: React.ReactNode;
  theme: NotificationTheme;
  onBannerClose?: () => void;
  isBannerHidden?: boolean;
  autoHide?: boolean;
  origin: FloatingBannerOrigin;
  hasFooter?: boolean;
  isUnifiedExperience?: boolean;
  hasSiteSelector?: boolean;
  hasMessages?: boolean;
  hasDirectSupportButton?: boolean;
};

const AUTO_HIDE_TIMEOUT = 10 * 1000; // 10 seconds

const mapThemeToSkin = (theme: NotificationTheme): SectionHelperSkin => {
  switch (theme) {
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'success':
      return 'success';
    case 'standard':
    default:
      return 'standard';
  }
};

const mapThemeToIcon = (theme: NotificationTheme) => {
  switch (theme) {
    case 'error':
      return <StatusAlert />;
    case 'warning':
      return <StatusWarning />;
    case 'success':
      return <StatusComplete />;
    case 'standard':
    default:
      return <StatusAlert />;
  }
};

export const FloatingBanner: FC<FloatingBannerProps> = ({
  theme,
  autoHide,
  children,
  onBannerClose,
  isBannerHidden = false,
  origin,
  hasFooter,
  isUnifiedExperience,
  hasSiteSelector,
  hasMessages,
  hasDirectSupportButton,
}) => {
  useEffect(() => {
    if (autoHide && !isBannerHidden && onBannerClose) {
      const timer = setTimeout(() => {
        onBannerClose();
      }, AUTO_HIDE_TIMEOUT);

      return () => clearTimeout(timer);
    }
  }, [autoHide, isBannerHidden, onBannerClose]);

  if (isBannerHidden) {
    return null;
  }

  return (
    <div
      className={classNames(s.floatingBanner, s[origin], { [s.hasFooter]: hasFooter, [s.unifiedExperience]: isUnifiedExperience && !hasSiteSelector, [s.siteSelector]: hasSiteSelector, [s.hasMessages]: hasMessages, [s.hasDirectSupportButton]: hasDirectSupportButton })}
      data-hook={floatingBannerDataHooks.root()}
    >
      <SectionHelper
        dataHook={floatingBannerDataHooks.sectionHelper()}
        skin={mapThemeToSkin(theme)}
        onClose={onBannerClose}
        showCloseButton={true}
        showPrefixIcon
        prefixIcon={mapThemeToIcon(theme)}
        size="medium"
        fullWidth
      >
        <div data-hook={floatingBannerDataHooks.content()}>{children}</div>
      </SectionHelper>
    </div>
  );
};
